/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useEffect } from 'react';
import { MapComponent, MapMarker, MapMarkerCluster, MapInfoWindow, eventUtil } from '@marriott/mi-ui-library';
import { GOOGLE_MAP_VAL_KEY } from '@marriott/shared/mi-helper-utils';
import { EmptyState } from '../EmptyState/EmptyState';
import { MapContainer } from '../../styled';
import { SmallPropertyCard, type RatingInfo } from '../../molecules/SmallPropertyCard/SmallPropertyCard';
import { getPropertyReviewUrl } from '../../utils';
import type { PropertyCardProperty } from '../../types';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { fetchSvgContent } from '@marriott/mi-shop-components/utils';
import { useSearchFormStore } from '../../store/searchFormStore';
import { USE_POINTS } from '../../store/store.constants';

function buildMapInfoWindowRatingInfo(hotelData: {
  id: string;
  seoNickname?: string;
  reviewUrl?: string;
  basicInformation?: PropertyCardProperty['basicInformation'];
  rating?: { stars?: number };
  reviews?: { numberOfReviews?: { count?: number } };
}): RatingInfo {
  const ratingValue = hotelData.rating?.stars || 0;
  const reviewCountValue = hotelData.reviews?.numberOfReviews?.count || 0;
  return {
    isMax: false,
    reviewUrl:
      getPropertyReviewUrl({
        id: hotelData.id,
        seoNickname: hotelData.seoNickname,
        reviewUrl: hotelData.reviewUrl,
        basicInformation: hotelData.basicInformation,
      } as PropertyCardProperty) || '#',
    reviewTitle: `${ratingValue.toFixed(1)} stars of ${reviewCountValue.toLocaleString()} reviews`,
    ratingValue,
    hasRating: Boolean(hotelData.rating?.stars),
    reviewCountValue,
  };
}

interface HotelMapProps {
  rawHotelData: any;
  hoveredHotelId: string | null;
}

export const HotelMap: React.FC<HotelMapProps> = ({ rawHotelData, hoveredHotelId }) => {
  // const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [center, setCenter] = useState({ lat: 37.7749, lng: -122.4194 });
  const [openInfoWindow, setOpenInfoWindow] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState<any>(null);
  const mapRef = useRef<any>(null);
  const clustererRef = useRef<any>(null);
  const [_isCardHovered, setIsCardHovered] = useState(false);
  // Check if user is searching with points/rewards redemption
  const usePointsState = useSearchFormStore((s: any) => s[USE_POINTS]);
  const isPointsSearch = usePointsState?.rewardsRedemption ?? false;

  eventUtil.on('propCardOver', property => {
    if (property) {
      setIsCardHovered(true);
      setSelectedMarker({
        code: property.id,
        label: property.basicInformation.nameInDefaultLanguage,
        latitude: property.basicInformation.latitude,
        longitude: property.basicInformation.longitude,
        count: 1,
        customLabel: property.basicInformation.nameInDefaultLanguage,
        hotelData: {
          id: property.id,
          name: property.basicInformation.nameInDefaultLanguage,
          seoNickname: property.seoNickname,
          reviewUrl: property.reviewUrl,
          imageUrl: property.media.primaryImage.edges?.[0]?.node.imageUrls.wideHorizontal,
          basicInformation: property.basicInformation,
          address: {
            city: 'New York', // From your test data
            country: 'US',
          },
          rating: {
            stars: property.reviews?.stars?.count || 4,
          },
          reviews: {
            numberOfReviews: {
              count: property.reviews?.numberOfReviews?.count || 0,
            },
          },
          distance: property.distance,
        },
      });
      setOpenInfoWindow(true);
      setCenter({
        lat: Number(property?.latitude),
        lng: Number(property?.longitude),
      });
    }
  });
  eventUtil.on('propCardUnOver', _property => {
    setIsCardHovered(false);
    setSelectedMarker(null);
    setOpenInfoWindow(false);
  });

  const [mapMarkers, setMapMarkers] = useState<any[]>([]);

  useEffect(() => {
    const fetchMarkers = async () => {
      const edges = rawHotelData?.data?.search?.conversationalSearch?.searchByGeolocation?.edges || [];
      const markers = await Promise.all(
        edges.map(async (edge: any) => {
          const property = edge.node.property;
          const rates = edge.node.rates?.rateModes?.[0]; // Get first rate object
          const svgStr = await fetchSvgContent(property.basicInformation.brand.id?.toLowerCase());

          return {
            code: property.id,
            label: property.basicInformation.nameInDefaultLanguage,
            latitude: property.basicInformation.latitude,
            longitude: property.basicInformation.longitude,
            count: 1,
            customLabel: property.basicInformation.nameInDefaultLanguage,
            hotelData: {
              id: property.id,
              name: property.basicInformation.nameInDefaultLanguage,
              seoNickname: property.seoNickname,
              reviewUrl: property.reviewUrl,
              svgStr,
              imageUrl: property.media.primaryImage.edges?.[0]?.node.imageUrls.wideHorizontal,
              basicInformation: property.basicInformation,
              rates: rates || {}, // Complete rate structure with amount/fees/taxes
              address: {
                city: 'New York', // From your test data
                country: 'US',
              },
              rating: {
                stars: property.reviews?.stars?.count || 4,
              },
              reviews: {
                numberOfReviews: {
                  count: property.reviews?.numberOfReviews?.count || 0,
                },
              },
              distance: property.distance,
            },
          };
        })
      );
      setMapMarkers(markers.filter((marker: any) => marker.latitude && marker.longitude));
    };

    fetchMarkers();
  }, [rawHotelData]);

  useEffect(() => {
    if (mapMarkers.length > 0) {
      const avgLat = mapMarkers.reduce((sum: number, marker: any) => sum + marker.latitude, 0) / mapMarkers.length;
      const avgLng = mapMarkers.reduce((sum: number, marker: any) => sum + marker.longitude, 0) / mapMarkers.length;
      setCenter({ lat: avgLat, lng: avgLng });
    }
  }, [mapMarkers]);

  useEffect(() => {
    if (hoveredHotelId && mapRef.current) {
      const hoveredMarker = mapMarkers.find(
        (marker: any) => marker.code === hoveredHotelId || marker.hotelData.id === hoveredHotelId
      );

      if (hoveredMarker) {
        const newCenter = { lat: hoveredMarker.latitude, lng: hoveredMarker.longitude };
        mapRef.current.panTo(newCenter);
        setSelectedMarker(hoveredMarker);
        setOpenInfoWindow(true);
      }
    } else if (!hoveredHotelId) {
      setOpenInfoWindow(false);
      setSelectedMarker(null);
    }
  }, [hoveredHotelId, mapMarkers]);

  const mapOptions = {
    fullscreenControl: true,
    mapTypeControl: true,
    zoomControl: true,
    gestureHandling: 'cooperative',
    streetViewControl: true,
  };

  const handleMarkerClick = (marker: any) => {
    setSelectedMarker(marker);
    setOpenInfoWindow(true);
  };

  const handleInfoWindowClose = () => {
    setOpenInfoWindow(false);
    setSelectedMarker(null);
  };

  // Helper function to create custom marker SVG
  const _createMarkerSvg = (marker: any, isHovered: boolean) => {
    const hotelName = marker.hotelData?.name || marker.label || 'Hotel';
    const rateData = marker.hotelData?.rates?.lowestAverageRate;

    // Format rate using proper decimal point handling
    let rateAmount = 'Rate N/A';
    if (rateData?.amount?.amount && rateData?.amount?.currency) {
      const formattedPrice = (rateData.amount.amount / 100).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      const currencySymbol = rateData.amount.currency === 'USD' ? '$' : rateData.amount.currency;
      rateAmount = `${currencySymbol}${formattedPrice}`;
    }

    const bgColor = '#ffffff';
    const borderColor = isHovered ? '#111827' : '#d1d5db';
    const textColor = '#111827';
    const shortName = hotelName.length > 20 ? hotelName.substring(0, 17) + '...' : hotelName;

    return `
      <svg width="120" height="50" viewBox="0 0 120 50" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="116" height="36" rx="6" ry="6" 
              fill="${bgColor}" stroke="${borderColor}" stroke-width="2" 
              filter="drop-shadow(2px 2px 4px rgba(0,0,0,0.2))"/>
        
        <!-- Hotel name -->
        <text x="60" y="16" font-family="Arial, sans-serif" font-size="10" 
              font-weight="600" fill="${textColor}" text-anchor="middle">
          ${shortName}
        </text>
        
        <!-- Rate -->
        <text x="60" y="30" font-family="Arial, sans-serif" font-size="11" 
              font-weight="700" fill="${isHovered ? '#ffffff' : '#059669'}" text-anchor="middle">
          ${rateAmount}
        </text>
        
        <!-- Pointer -->
        <polygon points="60,38 55,46 65,46" fill="${bgColor}" stroke="${borderColor}" stroke-width="1"/>
      </svg>
    `;
  };

  if (mapMarkers.length === 0) {
    return (
      <EmptyState icon="🗺️" title="No Hotel Locations" description="Hotels need location data to display on the map" />
    );
  }

  return (
    <MapContainer>
      <MapComponent
        googleMapsApiKey={GOOGLE_MAP_VAL_KEY}
        mapId="hotel-search-map"
        mapHeight="100%"
        center={center}
        zoom={12}
        options={mapOptions}
        onLoad={map => {
          // setIsMapLoaded(true);
          mapRef.current = map;
        }}
        onClick={() => setOpenInfoWindow(false)}
        mapContainerStyle={{
          position: 'static',
          overflow: 'visible',
          height: '100%',
          width: '100%',
        }}
      >
        <MapMarkerCluster
          options={{
            imagePath: '',
            styles: [
              {
                url:
                  'data:image/svg+xml;base64,' +
                  btoa(`
                    <svg xmlns="http://www.w3.org/2000/svg" width="73" height="73" viewBox="0 0 73 73">
                      <circle cx="36.5" cy="36.5" r="30" fill="#d1d5db" opacity="0.3" />
                      <circle cx="36.5" cy="36.5" r="20" fill="#ffffff" />
                    </svg>
                  `),
                textColor: 'black',
                width: 73,
                height: 73,
                textSize: 14,
                fontWeight: '500',
                className: 'cluster-icon',
              },
            ],
            gridSize: 60,
            maxZoom: 15,
          }}
          onLoad={clusterer => {
            clustererRef.current = clusterer;
          }}
        >
          {(clusterer: any) => (
            <>
              {mapMarkers.map((marker: any) => {
                const _isHovered = Boolean(
                  hoveredHotelId && (marker.code === hoveredHotelId || marker.hotelData.id === hoveredHotelId)
                );

                return (
                  <MapMarker
                    key={marker.code}
                    position={{ lat: marker.latitude, lng: marker.longitude }}
                    title={`${marker.hotelData?.name || marker.label} - ${marker.hotelData?.rates?.lowestAverageRate
                        ? `$${(marker.hotelData.rates.lowestAverageRate.amount?.amount / 100).toFixed(2)}`
                        : 'Rate N/A'
                      }`}
                    clusterer={clusterer}
                    onClick={() => handleMarkerClick(marker)}
                    label={{
                      text: '.',
                      fontSize: '22px',
                      className: `labels m-map-pin logo-background label-text m-map-pin-for-logo-container`,
                    }}
                    icon={{
                      url: `data:image/svg+xml,${encodeURIComponent(marker.hotelData.svgStr)}`,
                      scaledSize: new window.google.maps.Size(54, 36),
                    }}
                  />
                );
              })}
              {openInfoWindow && selectedMarker && (
                <MapInfoWindow
                  options={{ ariaLabel: 'hotel-info-window' }}
                  position={{ lat: selectedMarker.latitude, lng: selectedMarker.longitude }}
                  onCloseClick={handleInfoWindowClose}
                >
                  <SmallPropertyCard
                    imageUrl={selectedMarker.hotelData.imageUrl}
                    name={selectedMarker.hotelData.name}
                    ratingInfo={buildMapInfoWindowRatingInfo(selectedMarker.hotelData)}
                    location={selectedMarker.hotelData.distance}
                    propertyId={selectedMarker.code}
                    priceDisplayMode={isPointsSearch ? 'points' : 'cash'}
                  />
                </MapInfoWindow>
              )}
            </>
          )}
        </MapMarkerCluster>
      </MapComponent>
    </MapContainer>
  );
};
